import register from 'preact-custom-element';
import type { FunctionComponent } from 'preact';

import { getBoolProp } from '../../../utils/props';
import {
  CONSENT_REGIONS,
  normalizeConsentRegion,
  resolveConsentRegion,
} from '../../../domain/region';
import { ConsentView } from './ConsentView';

import '../../attribution/PoweredBySmileId';

/**
 * Detail payload of the `smileid-consent.granted` event. Consumers enrich this
 * into the full v3 `consent` part by adding `notice_language` and
 * `notice_privacy_policy_url` from their own config (host concerns, not the
 * screen's).
 */
export interface ConsentGrantedDetail {
  granted: true;
  granted_at: string;
}

declare global {
  interface WindowEventMap {
    'smileid-consent.granted': CustomEvent<ConsentGrantedDetail>;
    'smileid-consent.denied': CustomEvent<void>;
  }
}

interface Props {
  'theme-color'?: string;
  'hide-attribution'?: string | boolean;
  'partner-name'?: string;
  'partner-logo'?: string;
  'policy-url'?: string;
  /**
   * Region behaviour for the consent checkbox: `'eu'` starts un-ticked and
   * blocks Allow until ticked (regulated regions); `'non-eu'` may start
   * pre-ticked. Any other value (or none) auto-detects from the browser's
   * locale/timezone metadata via `lib/domain/region`, defaulting to the
   * compliance-safe `'eu'` behaviour when the signals are ambiguous.
   *
   * NOTE: the initial checkbox state is read on first render, so hosts must
   * set `consent-region` before inserting the element into the DOM.
   */
  'consent-region'?: string;
}

/**
 * Standalone, product-agnostic consent screen. Hosted as its own custom
 * element with its own build entry (`@smileid/web-sdk/consent`) so any
 * product (v3 SmartSelfie, DocV, Biometric KYC, …) can mount the region-aware
 * consent UX without pulling in the selfie/`smart-camera-web` bundle.
 *
 * Rendered only when the partner config omits a granted `consent_information`
 * — a partner-supplied consent skips this screen entirely.
 *
 * Events:
 *   - `smileid-consent.granted` — detail = `{ granted: true, granted_at }`
 *     (ISO 8601 timestamp stamped at grant time).
 *   - `smileid-consent.denied`  — user declined (no detail).
 */
const Consent: FunctionComponent<Props> = ({
  'theme-color': themeColor = '#001096',
  'hide-attribution': hideAttributionProp = false,
  'partner-name': partnerName,
  'partner-logo': partnerLogo,
  'policy-url': policyUrl,
  'consent-region': consentRegionProp,
}) => {
  const hideAttribution = getBoolProp(hideAttributionProp);
  const consentRegion =
    normalizeConsentRegion(consentRegionProp) ?? resolveConsentRegion();

  return (
    <ConsentView
      themeColor={themeColor}
      hideAttribution={hideAttribution}
      partnerName={partnerName}
      partnerLogo={partnerLogo}
      policyUrl={policyUrl}
      initialAccepted={consentRegion === CONSENT_REGIONS.NON_EU}
      onGranted={() => {
        window.dispatchEvent(
          new CustomEvent('smileid-consent.granted', {
            detail: { granted: true, granted_at: new Date().toISOString() },
          }),
        );
      }}
      onDenied={() => {
        window.dispatchEvent(new CustomEvent('smileid-consent.denied'));
      }}
    />
  );
};

if (!customElements.get('smileid-consent')) {
  register(
    Consent,
    'smileid-consent',
    [
      'theme-color',
      'hide-attribution',
      'partner-name',
      'partner-logo',
      'policy-url',
      'consent-region',
    ],
    { shadow: true },
  );
}

export default Consent;
